/**
 * Estimates API Service (TASK-048)
 *
 * API client for estimate operations: list, get, delete,
 * export, share, and bulk operations.
 */
import apiClient from './client';
import type {
  Estimate,
  EstimateDetail,
  EstimateFilters,
  EstimateMessage,
  PaginatedEstimateResponse,
  ExportResponse,
  ShareResponse,
  BulkDeleteResponse,
  BulkExportResponse,
  ComparisonResult,
  CompareRequest,
  ExportComparisonRequest,
  ExportComparisonResponse,
  InitDialogResponse,
} from '@/types/estimate';

export interface CreateEstimateRequest {
  query: string;
}

export const estimatesApi = {
  /**
   * Create a new estimate
   */
  async create(data: CreateEstimateRequest): Promise<Estimate> {
    const response = await apiClient.post<Estimate>('/api/v1/estimates/', data, {
      timeout: 120000, // AI generation can take 30-60s
    });
    return response.data;
  },

  /**
   * List estimates with optional filters
   */
  async list(filters?: EstimateFilters): Promise<PaginatedEstimateResponse> {
    const response = await apiClient.get<PaginatedEstimateResponse>(
      '/api/v1/estimates/',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get single estimate by ID with full details
   */
  async getById(id: string): Promise<EstimateDetail> {
    const response = await apiClient.get<EstimateDetail>(`/api/v1/estimates/${id}/`);
    return response.data;
  },

  /**
   * Delete an estimate
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/estimates/${id}/`);
  },

  /**
   * Export estimate to PDF or Excel
   */
  async export(id: string, format: 'pdf' | 'excel'): Promise<ExportResponse> {
    const response = await apiClient.post<ExportResponse>(
      `/api/v1/estimates/${id}/export/`,
      { format }
    );
    return response.data;
  },

  /**
   * Generate share link for estimate
   */
  async share(id: string): Promise<ShareResponse> {
    const response = await apiClient.post<ShareResponse>(
      `/api/v1/estimates/${id}/share/`
    );
    return response.data;
  },

  /**
   * Bulk delete multiple estimates
   */
  async bulkDelete(ids: string[]): Promise<BulkDeleteResponse> {
    const response = await apiClient.post<BulkDeleteResponse>(
      '/api/v1/estimates/bulk-delete/',
      { ids }
    );
    return response.data;
  },

  /**
   * Bulk export multiple estimates
   */
  async bulkExport(
    ids: string[],
    format: 'pdf' | 'excel'
  ): Promise<BulkExportResponse> {
    const response = await apiClient.post<BulkExportResponse>(
      '/api/v1/estimates/bulk-export/',
      { ids, format }
    );
    return response.data;
  },

  /**
   * Compare multiple estimates (TASK-066)
   */
  async compare(request: CompareRequest): Promise<ComparisonResult> {
    const response = await apiClient.post<ComparisonResult>(
      '/api/v1/estimates/compare/',
      request
    );
    return response.data;
  },

  /**
   * Export comparison results (TASK-066)
   */
  async exportComparison(
    request: ExportComparisonRequest
  ): Promise<ExportComparisonResponse> {
    const response = await apiClient.post<ExportComparisonResponse>(
      '/api/v1/estimates/compare/export/',
      request
    );
    return response.data;
  },

  /**
   * Get messages for an estimate (TASK-098)
   */
  async getMessages(estimateId: string): Promise<EstimateMessage[]> {
    const response = await apiClient.get<EstimateMessage[]>(
      `/api/v1/estimates/${estimateId}/messages/`
    );
    return response.data;
  },

  /**
   * Send a message in estimate brief-chat (TASK-098)
   */
  async sendMessage(estimateId: string, content: string): Promise<EstimateDetail> {
    const response = await apiClient.post<EstimateDetail>(
      `/api/v1/estimates/${estimateId}/messages/`,
      { content },
      { timeout: 120000 }  // AI response can take time
    );
    return response.data;
  },

  /**
   * Initialize estimate dialog (TASK-136)
   *
   * Creates a new estimate with welcome message containing
   * available services. Returns session_id for subsequent messages.
   */
  async initDialog(): Promise<InitDialogResponse> {
    const response = await apiClient.post<InitDialogResponse>(
      '/api/v1/estimates/init/'
    );
    return response.data;
  },
};

export default estimatesApi;
